import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Detects "Failed to fetch dynamically imported module" errors
 * which happen when the browser has cached old chunk hashes after a new deployment.
 */
function isDynamicImportError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message || "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    // Also catch generic chunk load failures
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk")
  );
}

/**
 * Clear all service worker caches and unregister the SW to force fresh load
 */
async function clearServiceWorkerCaches(): Promise<void> {
  try {
    // Clear all caches
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));

    // Tell the SW to skip waiting if there's a new one
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration?.waiting) {
      registration.waiting.postMessage("SKIP_WAITING");
    }
  } catch (e) {
    // Ignore errors — SW might not be available
  }
}

const RELOAD_COUNT_KEY = "smiths_reload_count";
const RELOAD_TS_KEY = "smiths_reload_ts";
const MAX_AUTO_RELOADS = 2;

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    // Auto-reload for stale dynamic import errors (up to MAX_AUTO_RELOADS times)
    if (isDynamicImportError(error)) {
      const lastReloadTs = sessionStorage.getItem(RELOAD_TS_KEY);
      const reloadCount = parseInt(
        sessionStorage.getItem(RELOAD_COUNT_KEY) || "0",
        10
      );
      const now = Date.now();

      // Reset counter if last reload was more than 60 seconds ago (new session/navigation)
      const timeSinceLastReload = lastReloadTs
        ? now - parseInt(lastReloadTs, 10)
        : Infinity;
      const effectiveCount =
        timeSinceLastReload > 60000 ? 0 : reloadCount;

      if (effectiveCount < MAX_AUTO_RELOADS) {
        sessionStorage.setItem(RELOAD_COUNT_KEY, String(effectiveCount + 1));
        sessionStorage.setItem(RELOAD_TS_KEY, String(now));
        // Clear SW caches first, then hard reload
        clearServiceWorkerCaches().then(() => {
          window.location.href =
            window.location.pathname + window.location.hash;
        });
        return;
      }
    }
  }

  handleManualReload = () => {
    // Clear the reload counter so manual reload gets fresh attempts
    sessionStorage.removeItem(RELOAD_COUNT_KEY);
    sessionStorage.removeItem(RELOAD_TS_KEY);
    // Clear SW caches and force hard reload
    clearServiceWorkerCaches().then(() => {
      window.location.href =
        window.location.pathname + "?_cb=" + Date.now() + window.location.hash;
    });
  };

  render() {
    if (this.state.hasError) {
      const isModuleError = isDynamicImportError(this.state.error);

      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-8">
            <AlertTriangle
              size={48}
              className="text-destructive mb-6 flex-shrink-0"
            />

            <h2 className="text-xl mb-4">An unexpected error occurred.</h2>

            {isModuleError && (
              <p className="text-sm text-muted-foreground mb-4 text-center">
                This usually happens after a site update. Clearing your browser
                cache or trying again in a moment should fix it.
              </p>
            )}

            <div className="p-4 w-full rounded bg-muted overflow-auto mb-6">
              <pre className="text-sm text-muted-foreground whitespace-break-spaces">
                {this.state.error?.message}
              </pre>
            </div>

            <button
              onClick={this.handleManualReload}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg",
                "bg-primary text-primary-foreground",
                "hover:opacity-90 cursor-pointer"
              )}
            >
              <RotateCcw size={16} />
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
